/**
 * Footer component.
 * @module components/theme/Footer/Footer
 */

import React, { useEffect, useRef, useState } from 'react';
import { Container, List, Segment } from 'semantic-ui-react';
import map from 'lodash/map';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL, addAppURL } from '@plone/volto/helpers/Url/Url';

import './Footer.css';

const messages = defineMessages({
  copyright: {
    id: 'Copyright',
    defaultMessage: 'Copyright',
  },
});

/**
 * Component to display the footer.
 * @function Footer
 * @param {Object} intl Intl object
 * @returns {string} Markup of the component
 */
const Footer = ({ intl }) => {
  const { siteActions = [] } = useSelector(
    (state) => ({
      siteActions: state.actions?.actions?.site_actions,
    }),
    shallowEqual,
  );
  const lowerFooterContainerRef = useRef(null);
  const siteActionsRef = useRef(null);
  const [isLowerFooterCentered, setIsLowerFooterCentered] = useState(false);

  useEffect(() => {
    const updateAlignment = () => {
      const siteActionsNode = siteActionsRef.current;
      if (!siteActionsNode) return;

      const links = Array.from(siteActionsNode.querySelectorAll('a'));
      if (links.length <= 1) {
        setIsLowerFooterCentered(false);
        return;
      }

      const firstRowTop = links[0].offsetTop;
      const hasWrappedSiteActions = links.some(
        (linkNode) => linkNode.offsetTop !== firstRowTop,
      );
      setIsLowerFooterCentered(hasWrappedSiteActions);
    };

    updateAlignment();

    const resizeObserver = new ResizeObserver(() => {
      updateAlignment();
    });

    if (lowerFooterContainerRef.current) {
      resizeObserver.observe(lowerFooterContainerRef.current);
    }
    if (siteActionsRef.current) {
      resizeObserver.observe(siteActionsRef.current);
    }

    window.addEventListener('resize', updateAlignment);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateAlignment);
    };
  }, []);

  return (
    <Segment
      role="contentinfo"
      vertical
      padded
      inverted
      color="grey"
      textAlign="center"
      id="footer"
      aria-label="Footer"
      tabIndex="-1"
    >
      <Segment id="footer">
        <div className="company-footer">
          <Container>
            <div className="footer-category-links">
              <div className="category-content">
                <div className="address">
                  <h3>Contact: </h3>
                  <p>
                    Prof. Dr. Maren Bennewitz<br></br>
                    Humanoid Robots Lab<br></br>
                    University of Bonn<br></br>
                    Friedrich-Hirzebruch-Allee 8<br></br>
                    53115 Bonn
                  </p>
                  <p>
                    Tel.: +49 (0) 228 73-54164<br></br>
                    Secr.: +49 (0) 228 73-4422<br></br>
                    Fax.: +49 (0) 228 73-4425<br></br>
                    Email: maren_at_cs.uni-bonn.de
                  </p>
                  <p>Office: 2.022</p>
                </div>
              </div>
            </div>
          </Container>

          <div className="upper-footer"></div>

          <div
            className={`lower-footer${isLowerFooterCentered ? ' is-centered' : ''}`}
          >
            <Container ref={lowerFooterContainerRef}>
              <div className="footer-nav-group">
                <div className="company-name">
                  <a href="https://www.uni-bonn.de">University of Bonn</a>
                </div>
                <div className="institute-name">
                  <a href="http://www.informatik.uni-bonn.de/">
                    Institute for Computer Science
                  </a>
                </div>
              </div>

              <div className="footer-nav-group">
                <div className="institute-name">
                  <a href="https://www.youtube.com/@HumanoidsBonn">Youtube</a>
                </div>
              </div>

              <div className="footer-nav-group site-actions" ref={siteActionsRef}>
                <a href="https://www.uni-bonn.de/en/imprint">Imprint</a>
                <a href="https://www.uni-bonn.de/en/data-protection-policy">
                  Data Protection Policy
                </a>
              </div>
            </Container>
          </div>
        </div>
      </Segment>
    </Segment>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Footer.propTypes = {
  /**
   * i18n object
   */
};

export default injectIntl(Footer);
